/* InstallContext — single global capture of PWA install signals.
   Before this, InstallPrompt, StickyInstallBar, and SmartInstallBanner each
   attached their own `beforeinstallprompt` listener. The event only fires
   ONCE per page, so the first to call preventDefault "stole" it and the
   other buttons couldn't trigger the native prompt anymore.

   This context captures the event exactly ONCE at app-mount and exposes:
     - canPrompt: true if the native Android prompt is available right now
     - promptInstall(): returns "accepted" | "dismissed" | "unavailable" | "redirected"
     - platform: "ios" | "android" | "desktop" | "other"
     - isStandalone: bool
     - installed: true after appinstalled event
     - playStoreUrl: canonical Play Store deep link for the native TWA

   IMPORTANT (Aug 2026): ORIO is live on Google Play as `com.orio.app`.
   Android users installing from the web should get the *native* app, not
   the PWA. So `promptInstall()` on Android hard-redirects to the Play
   Store and every install CTA in the app can use `playStoreUrl` directly.
*/
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

/* Canonical Play Store URL for the ORIO Android TWA (com.orio.app).
   Import via `useInstall().playStoreUrl` or the exported constant so we
   only ever have to update this in one place. */
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.orio.app";

const InstallCtx = createContext(null);

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/i.test(ua)) return "android";
  // Very basic desktop detection
  if (/Macintosh|Windows|Linux/i.test(ua)) return "desktop";
  return "other";
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true
  );
}

export function InstallProvider({ children }) {
  // The deferred BeforeInstallPromptEvent — the whole reason this context exists.
  const deferredRef = useRef(null);
  const [canPrompt, setCanPrompt] = useState(false);
  const [platform] = useState(detectPlatform);
  const [installed, setInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onBip = (e) => {
      // Prevent the mini-infobar on mobile Chrome, we'll show our own UI.
      e.preventDefault();
      deferredRef.current = e;
      setCanPrompt(true);
    };

    const onInstalled = () => {
      deferredRef.current = null;
      setCanPrompt(false);
      setInstalled(true);
      setIsStandalone(true);
      // Fire-and-forget analytics ping (endpoint already exists).
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/install-analytics/event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event: "installed", platform, standalone: true }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* noop */ }
    };

    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [platform]);

  const promptInstall = useCallback(async () => {
    // Analytics ping (fire-and-forget).
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/install-analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "cta_clicked", platform, standalone: false }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* noop */ }

    // Path 0 — ANDROID: ORIO ships as a native TWA on Google Play. Any
    // install CTA on Android must open the Play Store listing, never the
    // Chrome PWA install. The Play Store link opens the Play app if
    // installed (Android Intent handling) or the web listing otherwise.
    if (platform === "android" && !isStandalone) {
      try {
        window.location.href = PLAY_STORE_URL;
      } catch { /* noop */ }
      return "redirected";
    }

    // Path A — Modern Web Install API (Chrome/Edge 143+, desktop stable,
    // mobile rolling out). Available without user gesture chain issues.
    // When it lands on Android Chrome we get one-tap install automatically.
    if (typeof navigator !== "undefined" && typeof navigator.install === "function") {
      try {
        const result = await navigator.install();
        // Success — `manifest_id` present.
        if (result && (result.manifest_id || result.manifestId)) {
          return "accepted";
        }
      } catch (err) {
        // AbortError = user rejected. Other errors → fall through to bip.
        if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
          return "dismissed";
        }
        // else: fall through to beforeinstallprompt path
      }
    }

    // Path B — Legacy beforeinstallprompt (Android/desktop Chrome<143).
    const evt = deferredRef.current;
    if (!evt) return "unavailable";
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      deferredRef.current = null;
      setCanPrompt(false);
      return choice?.outcome || "dismissed";
    } catch {
      deferredRef.current = null;
      setCanPrompt(false);
      return "unavailable";
    }
  }, [platform, isStandalone]);

  const value = {
    // Consider "canPrompt" true if:
    // - Android non-standalone (we deep-link to Play Store)
    // - navigator.install exists (Chrome/Edge 143+)
    // - or beforeinstallprompt already fired.
    canPrompt:
      (platform === "android" && !isStandalone) ||
      canPrompt ||
      (typeof navigator !== "undefined" && typeof navigator.install === "function"),
    promptInstall,
    platform,
    isStandalone,
    installed,
    playStoreUrl: PLAY_STORE_URL,
  };

  return <InstallCtx.Provider value={value}>{children}</InstallCtx.Provider>;
}

export function useInstall() {
  const ctx = useContext(InstallCtx);
  if (!ctx) {
    // Safe fallback so components using the hook don't crash if provider is
    // temporarily missing (e.g. tests, storybook).
    return {
      canPrompt: false,
      promptInstall: async () => "unavailable",
      platform: detectPlatform(),
      isStandalone: detectStandalone(),
      installed: false,
      playStoreUrl: PLAY_STORE_URL,
    };
  }
  return ctx;
}
